import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { IInvestor } from "../services/investor/investorService";
import DefaultAvatar from "../assets/images/default-avatar.webp";
import { SPACES_ENDPOINT } from "../utils/constants";
import { Preferences } from "@capacitor/preferences";

const STORAGE_NAME = "investor-detail-storage";

interface InvestorDetailState {
  investor: IInvestor | null;
  avatar: string;
  isLoading: boolean;
  isInitialLoaded: boolean;
  setInvestor: (investor: IInvestor) => void;
  updateInvestor: (updates: Partial<IInvestor>) => void;
  setAvatar: (avatar: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsInitalLoaded: (isInitialLoaded: boolean) => void;
  reset: () => void;
}

const capacitorPreferencesStorage = {
  getItem: async (key: string) => {
    const { value } = await Preferences.get({ key });
    return value ?? null;
  },
  setItem: (key: string, value: string) => {
    return Preferences.set({ key, value });
  },
  removeItem: (key: string) => {
    return Preferences.remove({ key });
  },
};

const useInvestorDetailStore = create<InvestorDetailState>()(
  devtools(
    persist(
      (set) => ({
        investor: null,
        avatar: DefaultAvatar,
        isLoading: true,
        isInitialLoaded: false,
        setInvestor: (investor: IInvestor) => {
          set({
            investor: investor,
            avatar:
              investor.avatar && investor.avatar.length > 0
                ? `${SPACES_ENDPOINT}${investor.avatar}`
                : DefaultAvatar,
          });
        },
        updateInvestor: (updates: Partial<IInvestor>) => {
          set((state) => {
            if (!state.investor) return state;
            
            const updatedInvestor = {
              ...state.investor,
              ...updates
            };
            
            return {
              ...state,
              investor: updatedInvestor,
              avatar: updatedInvestor.avatar && updatedInvestor.avatar.length > 0
                ? `${SPACES_ENDPOINT}${updatedInvestor.avatar}`
                : DefaultAvatar,
            };
          });
        },
        setAvatar: (avatar: string) => set({ avatar }),
        setIsLoading: (isLoading: boolean) => set({ isLoading }),
        setIsInitalLoaded: (isInitialLoaded: boolean) =>
          set({ isInitialLoaded }),
        reset: () => {
          set({ investor: null, avatar: DefaultAvatar, isLoading: true, isInitialLoaded: false });
        },
      }),
      {
        name: STORAGE_NAME,
        storage: createJSONStorage(() => capacitorPreferencesStorage),
      }
    )
  )
);

export default useInvestorDetailStore;
